import { fetchDxApi, fetchDxApiAuthenticated } from "./fetchStudioApi";
import { OnboardingUserState } from 'data/DXApp/DXApp';
import { getNonAuthUserId, getUserId } from './fetchStudioApi';

export interface PaymentOption {
    id: string;
    price: number;
    currency?: string;
    duration?: number;
    durationUnit?: string;
    durationValue?: number;
    durationType?: string;
    monthlyPrice?: number;
    selected?: boolean;
    description?: string;
    displayName?: string;
    appId?: string;
    planCategory?: string;
    priceId?: string;
    isRecurring?: boolean;
    paymentProcessor?: string;
    features?: string[];
}

export interface PaymentOptionsResponse {
    success: boolean;
    options?: PaymentOption[];
    defaultSelectedId?: string;
    transformationDate?: string;
    selectPlanText?: string;
    transformationPromiseByDate?: string;
    transformationBubbleText?: string;
    continuousAnnualPromise?: string;
    message?: string;
    error?: string;
}

export interface ProcessPaymentRequest {
    userId: string;
    appId: string;
    priceOptionId: string;
    paymentMethodId: string;
    metadata?: Record<string, any>;
}

export interface PaymentProcessResponse {
    success: boolean;
    transactionId?: string;
    subscriptionId?: string;
    clientSecret?: string;
    message?: string;
    error?: string;
}

export interface CheckoutSessionRequest {
    userId: string;
    storefrontSlug: string;
    appSlug: string;
    priceOptionId: string;
    successUrl: string;
    cancelUrl: string;
    metadata?: Record<string, any>;
}

export interface CheckoutSessionResponse {
    success: boolean;
    sessionId?: string;
    checkoutUrl?: string;
    message?: string;
    error?: string;
}

/**
 * Gets the available payment options for a user for a specific app
 */
export const getPaymentOptions = async (
    storefrontSlug: string,
    appSlug: string,
    authUser: any = null
): Promise<PaymentOptionsResponse> => {
    // Get appropriate user ID (auth or non-auth)
    const userId = getUserId(authUser);



    try {
        const response = await fetchDxApi({
            path: `/payments/payment-options/${storefrontSlug}/${appSlug}?user_id=${userId}`,
            method: "GET",
            nonAuthUserId: userId,
            authUser,
        });


        console.log("API pricing logic DEBUG:", {
            selectPlanText: response?.response?.selectPlanText,
            transformationPromiseByDate: response?.response?.transformationPromiseByDate,
            transformationBubbleText: response?.response?.transformationBubbleText,
            continuousAnnualPromise: response?.response?.continuousAnnualPromise
        });


        // Check if the response has the expected nested structure
        if (response?.status === "success" && response?.response) {
            // Extract options from the nested response
            const {
                payment_options,
                transformation_date,
                selectPlanText,
                transformationPromiseByDate,
                transformationBubbleText,
                continuousAnnualPromise,
                prices_in_cents = true  // Default to true for backward compatibility
            } = response.response;

            // Clean up excessive logging
            console.log("Extracted values:", {
                payment_options: payment_options?.length,
                transformation_date,
                selectPlanText,
                transformationPromiseByDate,
                transformationBubbleText,
                continuousAnnualPromise,
                prices_in_cents
            });

            // Map backend options to our standard format
            const processedOptions = payment_options.map((option: any) => {
                // Only convert from cents to dollars if prices are in cents
                const price = prices_in_cents ? option.price / 100 : option.price;
                let duration = option.duration || option.durationValue || 6;
                let durationUnit = option.durationUnit || option.durationType?.toLowerCase() || 'weeks';

                let monthlyPrice = option.monthlyPrice;
                if (!monthlyPrice) {
                    const durationInMonths = durationUnit === 'weeks' ? duration / 4 : duration;
                    monthlyPrice = Math.round((price / durationInMonths) * 100) / 100;
                } else {
                    // If monthlyPrice is provided, ensure it's also converted if needed
                    monthlyPrice = prices_in_cents ? monthlyPrice / 100 : monthlyPrice;
                }

                return {
                    id: option.id,
                    price: price,
                    currency: option.currency || 'USD',
                    duration,
                    durationUnit,
                    monthlyPrice,
                    selected: option.id === response.defaultSelectedId,
                    description: option.displayName || option.description || '6-Week Transformation Plan',
                    features: option.features || [],
                    planCategory: option.planCategory,
                    displayName: option.displayName,
                    appId: option.appId,
                    priceId: option.priceId,
                    isRecurring: option.isRecurring,
                    paymentProcessor: option.paymentProcessor
                };
            });

            // If there are options, select the first one by default
            if (processedOptions.length > 0) {
                processedOptions[0].selected = true;
            }

            return {
                success: true,
                options: processedOptions,
                defaultSelectedId: processedOptions[0]?.id,
                transformationDate: transformation_date,
                selectPlanText: selectPlanText,
                transformationPromiseByDate: transformationPromiseByDate,
                transformationBubbleText: transformationBubbleText,
                continuousAnnualPromise: continuousAnnualPromise
            };
        }

        // If the response doesn't have the expected structure
        return {
            success: false,
            options: [],
            message: "Invalid response format"
        };
    } catch (error) {
        console.error("Error fetching payment options:", error);
        return {
            success: false,
            options: [],
            message: "Failed to fetch payment options"
        };
    }
};

/**
 * Processes a payment for an app using the selected payment option
 */
export const processPayment = async (
    userId: string,
    appId: string,
    priceOptionId: string,
    paymentMethodId: string,
    metadata?: Record<string, any>
): Promise<PaymentProcessResponse> => {
    try {
        const request: ProcessPaymentRequest = {
            userId,
            appId,
            priceOptionId,
            paymentMethodId,
            metadata
        };

        const response = await fetchDxApi({
            path: `/payments/process-payment`,
            method: "POST",
            body: JSON.stringify(request),
            isJsonPayload: true
        });



        // Check if the response has the expected structure
        if (response && response.status === "success" && response.response) {
            return response.response;
        }

        // If the response doesn't have the expected structure
        return {
            success: false,
            error: "Invalid response format"
        };
    } catch (error) {
        console.error("Error processing payment:", error);
        return {
            success: false,
            error: "Failed to process payment"
        };
    }
};

/**
 * Cancels a subscription for a user
 */
export const cancelSubscription = async (
    subscriptionId: string,
    authUser: any = null
): Promise<{ success: boolean; message?: string }> => {
    try {
        // Get appropriate user ID (auth or non-auth)
        const userId = getUserId(authUser);

        const response = await fetchDxApi({
            path: `/payments/cancel-subscription/${subscriptionId}?user_id=${userId}`,
            method: "POST",
            nonAuthUserId: userId,
            authUser
        });



        // Check if the response has the expected structure
        if (response && response.status === "success" && response.response) {
            return response.response;
        }

        // If the response doesn't have the expected structure
        return {
            success: false,
            message: "Invalid response format"
        };
    } catch (error) {
        console.error("Error cancelling subscription:", error);
        return {
            success: false,
            message: "Failed to cancel subscription"
        };
    }
};

/**
 * Creates a checkout session for the Stripe payment process
 */
export const createCheckoutSession = async (
    userId: string,
    storefrontSlug: string,
    appSlug: string,
    priceOptionId: string,
    successUrl: string,
    cancelUrl: string,
    metadata?: Record<string, any>,
    authUser: any = null
): Promise<CheckoutSessionResponse> => {
    try {
        // Get appropriate user ID (auth or non-auth)
        const nonAuthUserId = getUserId(authUser);

        // Use the userId parameter if provided, otherwise use the current user ID
        const actualUserId = userId || nonAuthUserId;

        // Merge email from authUser if available and not already in metadata
        let finalMetadata = { ...metadata };
        if (authUser && authUser.attributes?.email && (!finalMetadata || !finalMetadata.email)) {
            finalMetadata = {
                ...finalMetadata,
                email: authUser.attributes.email
            };

        }

        // If authUser has a name, include it as well
        if (authUser && authUser.attributes?.name && (!finalMetadata || !finalMetadata.name)) {
            finalMetadata = {
                ...finalMetadata,
                name: authUser.attributes.name
            };

        }

        const request: CheckoutSessionRequest = {
            userId: actualUserId,
            storefrontSlug,
            appSlug,
            priceOptionId,
            successUrl,
            cancelUrl,
            metadata: finalMetadata
        };

        const response = await fetchDxApi({
            path: `/payments/create-checkout-session`,
            method: "POST",
            body: JSON.stringify(request),
            isJsonPayload: true,
            nonAuthUserId,
            authUser
        });



        // Check if the response has the expected structure
        if (response && response.status === "success" && response.response) {
            return response.response;
        }

        // If the response doesn't have the expected structure
        return {
            success: false,
            error: "Invalid response format"
        };
    } catch (error) {
        console.error("Error creating checkout session:", error);
        return {
            success: false,
            error: "Failed to create checkout session"
        };
    }
};

/**
 * Gets the Studio App Builder payment options
 */
export const getStudioPaymentOptions = async (): Promise<any> => {
    try {
        const response = await fetchDxApiAuthenticated({
            path: '/payments/studio-payment-options',
            method: 'GET',
            isJsonPayload: false
        });

        if (response && response.status === "success" && response.response) {
            return response.response;
        }

        return {
            success: false,
            error: "Invalid response format"
        };
    } catch (error) {
        console.error("Error fetching studio payment options:", error);
        return {
            success: false,
            error: "Failed to fetch studio payment options"
        };
    }
};

/**
 * Creates a checkout session for Studio App Builder subscription
 */
export const createStudioCheckoutSession = async (data: {
    priceOptionId: string;
    tierSlug: string;
}): Promise<CheckoutSessionResponse> => {
    try {
        const baseUrl = window.location.origin;
        const successUrl = `${baseUrl}/dashboard/builder?mode=publish&payment_success=true&tier=${data.tierSlug}`;
        const cancelUrl = `${baseUrl}/dashboard/builder?mode=pricing_tiers`;

        const response = await fetchDxApiAuthenticated({
            path: '/payments/create-studio-checkout-session',
            method: 'POST',
            body: JSON.stringify({
                price_option_id: data.priceOptionId,
                success_url: successUrl,
                cancel_url: cancelUrl,
                tier_slug: data.tierSlug
            }),
            isJsonPayload: true
        });

        if (response && response.status === "success" && response.response) {
            return response.response;
        }

        return {
            success: false,
            error: "Invalid response format"
        };
    } catch (error) {
        console.error("Error creating studio checkout session:", error);
        return {
            success: false,
            error: "Failed to create checkout session"
        };
    }
};

/**
 * Gets the user's active Studio subscriptions
 */
export const getUserStudioSubscriptions = async (): Promise<any> => {
    try {
        const response = await fetchDxApiAuthenticated({
            path: '/payments/user-studio-subscriptions',
            method: 'GET',
            isJsonPayload: false
        });

        if (response && response.status === "success" && response.response) {
            return response.response;
        }

        return {
            subscriptions: [],
            hasActiveSubscription: false
        };
    } catch (error) {
        console.error("Error fetching user studio subscriptions:", error);
        return {
            subscriptions: [],
            hasActiveSubscription: false
        };
    }
};
